import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ClientPlatform } from "@omnara/shared-js/sloplog";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns the client platform kind for relay connections.
 * Detects Electron (desktop app) vs browser (web app).
 */
export function getClientKind(): ClientPlatform {
  return getElectronAPI() ? "desktop" : "web";
}

/**
 * Minimal subset of the Electron preload bridge used by shared (web + desktop)
 * code. The full ElectronAPI interface lives in the desktop app's type
 * declarations which are not visible to the web app's tsconfig. This keeps the
 * helper properly typed in both builds without cross-project type dependencies.
 */
interface ElectronBridge {
  setResolvedTheme?: (theme: string) => Promise<void>;
  openExternal?: (url: string) => Promise<void>;
  openDirectoryDialog?: () => Promise<string | null>;
  getCurrentMachineId?: (payload: { userId: string }) => Promise<{ machineId: string | null }>;
  startDaemon?: () => Promise<void>;
  getPlatformInfo?: () => Promise<{ hostname: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/**
 * Returns the electronAPI bridge if running in the desktop app, or null in the
 * web app. Centralises the typeof/in guard so callers don't need ad-hoc casts.
 */
export function getElectronAPI(): ElectronBridge | null {
  return typeof window !== "undefined" && "electronAPI" in window
    ? (window.electronAPI as ElectronBridge)
    : null;
}

// ---------------------------------------------------------------------------
// GitHub OAuth helpers (desktop vs web)
// ---------------------------------------------------------------------------

/**
 * Returns the current in-app route so OAuth can continue back to the same view.
 */
export function getCurrentReturnPath(fallback = "/dashboard"): string {
  if (typeof window === "undefined") {
    return fallback;
  }
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return path.startsWith("/") ? path : fallback;
}

/**
 * Opens a URL in the system browser (desktop) or navigates in the current
 * tab (web). Falls back to in-tab navigation if openExternal fails.
 */
export function openExternalUrl(url: string): void {
  const electron = getElectronAPI();
  if (electron?.openExternal) {
    electron.openExternal(url).catch(() => {
      window.location.href = url;
    });
  } else {
    window.location.href = url;
  }
}

const WEB_APP_URL =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) || "https://omnara.com";

export function openBillingExternallyOnDesktop(): boolean {
  if (getClientKind() !== "desktop") {
    return false;
  }

  openExternalUrl(`${WEB_APP_URL}/billing`);
  return true;
}
